import { ICommentsTableProps, ICommentTableEntry } from '../CommentsTable';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { SelectOption } from '../../../../tailwindComponents/selectTW/interfaces';
import { SeeMoreLessSize, TextTag } from '../../../../ui-toolkit';
import { customFilter } from 'react-bootstrap-table2-filter';
import { EditCommentType } from '../EditCommentType';
import { SeeMoreLessTW } from '../../../../tailwindComponents/seeMoreLessTW/SeeMoreLessTW';
import { useRouteMatch } from 'react-router';
import { GfmRenderer } from '../../../../tailwindComponents/markdown/GfmRenderer';
import { DateFormat, getDateString } from '../../../../ui-toolkit/utilities/getDateString';

const filterDelay = 300;

export const useGetCommentsTableColumns = ({
  comments,
  commentTypeOptions,
  onEditCommentType,
}: ICommentsTableProps): IBootstrapTableColumnProps<ICommentTableEntry>[] => {
  const { path } = useRouteMatch();

  return useMemo(() => {
    const isInternalCommentsTable = path.endsWith('internal');
    const postedByOptions: SelectOption[] = _(comments)
      .uniqBy(({ postedBy }) => postedBy)
      .map(({ postedBy }) => ({ label: postedBy, value: postedBy }))
      .value();
    const commentTypes: SelectOption[] = _(comments)
      .uniqBy(({ commentTypeDisplay }) => commentTypeDisplay)
      .map(({ commentTypeDisplay }) => ({ label: commentTypeDisplay, value: commentTypeDisplay }))
      .value();

    return [
      {
        text: 'Date Posted',
        dataField: 'datePosted',
        sort: true,
        formatter: (_, row) => <>{getDateString(DateFormat.hugeDateTime, row.datePosted, true)}</>,
        headerStyle: { width: 240 },
      },
      {
        text: 'Comment Type',
        dataField: 'commentTypeDisplay',
        sort: true,
        filter: customFilter({
          type: 'SELECT',
          options: commentTypes,
        }),
        formatter: (_, row) =>
          (commentTypeOptions && onEditCommentType && (
            <EditCommentType
              commentId={row.id}
              commentType={row.commentType!}
              commentTypeOptions={commentTypeOptions}
              onEditCommentType={onEditCommentType}
            />
          )) ||
          (row.commentTypeColor && <TextTag type={row.commentTypeColor} text={row.commentTypeDisplay} />) ||
          row.commentTypeDisplay,
        headerStyle: { width: 160 },
      },
      {
        text: 'Posted By',
        sort: true,
        filter: customFilter({
          type: 'SELECT',
          options: postedByOptions,
        }),
        dataField: 'postedBy',
        headerStyle: { width: 160 },
      },
      {
        text: 'Comment',
        dataField: 'content',
        filter: customFilter({
          delay: filterDelay,
          type: 'TEXT',
        }),
        formatter: (_, row) => {
          if (!isInternalCommentsTable) {
            return <SeeMoreLessTW maxCollapsedHeight={SeeMoreLessSize.regular}>{row.content}</SeeMoreLessTW>;
          }
          return (
            <SeeMoreLessTW maxCollapsedHeight={SeeMoreLessSize.regular}>
              <GfmRenderer markdownText={row.content}></GfmRenderer>
            </SeeMoreLessTW>
          );
        },
      },
    ];
  }, [comments, commentTypeOptions, onEditCommentType, path]);
};
